/* eslint-disable import/no-named-as-default-member */
import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { withRouter, Link } from "react-router-dom";
import styled from "styled-components";
import { Lock, Edit as EditIcon } from "@material-ui/icons";
import { Toolbar, Button, Grid, Tooltip } from "@material-ui/core";
import queryString from "query-string";

import BreadCrumbGrid from "../Global/BreadCrumb/BreadCrumbGrid";
import {
  SecondaryButton,
  NotesButton,
  AttachmentsButton,
  CancelButton,
  SaveButton,
  SaveAndReturnButton,
  DuplicateButton,
  DeleteButton
} from "../Global/Buttons";
import LoanAttachments from "../Global/OptionsMenu/LoanTable/LoanAttachments";
import LoanNotes from "../Global/OptionsMenu/LoanTable/LoanNotes";
import AddEditAlert from "../Notifications/AddEditAlert";
import SimpleInfoDialog from "../Global/ConfirmationDialogs/SimpleInfoDialog";
import QuoteDuplicationConfirmationDialog from "../Global/ConfirmationDialogs/QuoteDuplicationConfirmationDialog";
import { getNotificationsByDeal } from "../../store/state/notifications";
import { updateLoanStatusById, getLoanModificationHistory } from "../../store/state/loan/loan";
import loanStatuses from "../../constants/loanStatuses";
import dealStatuses from "../../constants/status";

const Title = styled.div`
  font-size: 24px;
  color: #333333;
`;

const VerticalDivider = styled.div`
  height: 20px;
  background: #d7d7d7;
  width: ${props => (props.width ? `${props.width}px` : "1px")};
`;

const Spacer = styled.div`
  flex-grow: 1;
`;

const NewLockedButton = styled(Button)`
  && {
    margin-left: 4px;
    padding: 8px 8px 8px 2px;
    border-radius: 0;
    color: #888888;
    background: none;
  }
  & span {
    margin-left: 4px;
  }
  &&:hover {
    background: #f6f6f6;
  }
`;

const StyledDelete = styled.div`
  && {
    float: right;
  }
`;
const ShadowyPageWrapper = styled.div`
  && {
    box-shadow: 0 0 3px grey;
    padding: 15px 10px;
    margin-bottom: 16px;
  }
`;

const OrganicLink = ({ to, targetId, children, placeholder, ...other }) => {
  if (targetId === "free") return null;
  return (
    <Link to={to} {...other}>
      {children}
    </Link>
  );
};

class LoanDetailsToolbar extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isAttachmentsOpen: false,
      isNotesOpen: false,
      popupOpen: false,
      isCloneDialogOpen: false
    };

    this.loanModificationHistoryRequested = false;
  }

  componentDidUpdate() {
    /*
     * NOTE:  This probably looks weird but I can't call this method in "componentDidMount" because the dealId and loanId are not available.  So, I have
     *        to call it from this method which means I need some way to keep track of whether or not it has been called before.
     *        -- Jonathan Byrne 04/03/2020
     */
    if (this.props.deal.id !== "free" && this.props.loan.id !== "free" && !this.loanModificationHistoryRequested) {
      this.loanModificationHistoryRequested = true;
      this.props.getLoanModificationHistory(this.props.deal.id, this.props.loan.id);
    }
  }

  renderEditButton = isQuote => {
    if (!this.props.loan.isInModification) {
      return (
        <SecondaryButton onClick={this.props.onOpenEditMode}>
          <EditIcon />
          {isQuote ? <div>EDIT QUOTE</div> : <div>EDIT LOAN</div>}
        </SecondaryButton>
      );
    }
    return (
      <Tooltip title="This loan is being modified and cannot be edited.">
        <span>
          <SecondaryButton disabled>
            <EditIcon />
            {isQuote ? <div>EDIT QUOTE</div> : <div>EDIT LOAN</div>}
          </SecondaryButton>
        </span>
      </Tooltip>
    );
  };

  renderFeatureLockedModel = () => {
    return (
      <SimpleInfoDialog
        title="Feature Locked"
        message="This feature is only available if you upgrade your account."
        modalOpen={this.state.popupOpen}
        handleClose={this.closePopup}
        handleAccept={this.closePopup}
        secondaryContent={(
          <p>
            If you would to schedule a demo to view these features, contact <a href="mailto:sales@loanboss.com">sales@loanboss.com</a>. <br /> <br />
            Please contact your LoanBoss Administrator to upgrade or if you believe you are seeing this message in error:{" "}
            <a href="mailto:support@loanboss.com">support@loanboss.com</a>.
          </p>
        )}
      />
    );
  };

  AddAlertButton = onClick => {
    if (this.isProTier()) {
      return (
        <SecondaryButton onClick={onClick} disableRipple>
          ALERTS
        </SecondaryButton>
      );
    }
    return (
      <NewLockedButton onClick={this.openPopup} disableRipple>
        <Lock />
        ALERTS
      </NewLockedButton>
    );
  };

  SetReminderButton = onClick => {
    if (this.isProTier()) {
      return (
        <SecondaryButton onClick={onClick} disableRipple>
          REMINDERS
        </SecondaryButton>
      );
    }
    return (
      <NewLockedButton onClick={this.openPopup} disableRipple>
        <Lock />
        REMINDERS
      </NewLockedButton>
    );
  };

  openNotes = () => this.setState({ isNotesOpen: true });

  closeNotes = () => this.setState({ isNotesOpen: false });

  openAttachments = () => this.setState({ isAttachmentsOpen: true });

  closeAttachments = () => this.setState({ isAttachmentsOpen: false });

  renderViewModeButtons = isQuote => {
    if (!this.props.isEditing) {
      return (
        <>
          {(!this.props.authentication.isGuest || (this.props.authentication.isGuest && this.props.guestPermissions.canEdit)) && (
            <>
              {this.renderEditButton(isQuote)}
              <VerticalDivider />
            </>
          )}
          {!isQuote && this.props.includeAlertButtons && (
            <>
              {this.AddAlertButton(() => {
                this.props.getNotificationsByDeal({ dealId: this.props.deal.id });
                this.props.addAlert();
              })}
              <VerticalDivider />
              {this.SetReminderButton(() => {
                this.props.getNotificationsByDeal({ dealId: this.props.deal.id });
                this.props.setReminder();
              })}
            </>
          )}
          {(!this.props.authentication.isGuest || (this.props.authentication.isGuest && this.props.guestPermissions.canEdit)) && isQuote && (
            <>
              <DuplicateButton onClick={this.onDuplicate} />
              {!this.props.isEditing && isQuote && (
                <>
                  <VerticalDivider />
                  <SecondaryButton
                    onClick={() =>
                      this.props.history.push(
                        `/portfolio/funds/free/deals/${this.props.deal.id}/cashflowsPage?showActiveLoans=true&loanId=${this.props.loan.id}&showQuotes=true`
                      )}
                  >
                    Cashflows
                  </SecondaryButton>
                </>
              )}
            </>
          )}
        </>
      );
    }
  };

  renderEditModeButtons = () => {
    if (!this.props.isEditing) {
      return null;
    }
    return (
      <>
        <CancelButton onClick={this.props.onCancel} />
        <SaveButton onClick={this.props.onSave} />
        <SaveAndReturnButton id="saveAndReturnButton" onClick={this.props.onSaveAndReturn} />
      </>
    );
  };

  onDuplicate = () => {
    const { loan, loanStatusId } = this.props;
    this.props.updateLoanStatusById({
      loanId: loan.id,
      loanStatusId,
      callback: () => {
        this.setState({ isCloneDialogOpen: true });
      }
    });
  };

  isProTier = () => {
    if (this.props.authentication) {
      return this.props.authentication.companyLevel === "Professional" 
      || this.props.authentication.companyLevel === "Enterprise"
      || this.props.authentication.companyLevel==="Secondary Sponsor";
    }
    return false;
  };

  openPopup = () => {
    this.setState({ popupOpen: true });
  };

  closePopup = () => {
    this.setState({ popupOpen: false });
  };

  closeCloneDialog = () => {
    this.setState({ isCloneDialogOpen: false });
  };

  cloneLoan = () => {
    this.closeCloneDialog();
    this.props.history.push(`/loan/${this.props.match.params.dealId}/${this.props.clone.id}`);
  };

  showModificationHistory = () => {
    const query = queryString.stringify(
      {
        isModificationHistory: true,
        primaryLoanId: this.props.loan.id
      },
      {
        arrayFormat: "bracket"
      }
    );
    this.props.history.push(`/pipeline/deal/${this.props.deal.id}/quote-comparison?${query}`);
  };

  render() {
    const { fund, deal, loan, authentication, quoteConditions } = this.props;
    const loanId = loan.id;
    const { isAttachmentsOpen, isNotesOpen } = this.state;

    const hasLoanModificationHistory =
      this.props.loanModificationHistory && this.props.loanModificationHistory[loanId] && this.props.loanModificationHistory[loanId].length > 1;

    return (
      <ShadowyPageWrapper>
        <AddEditAlert
          key={this.props.match.params.dealId}
          dealId={this.props.match.params.dealId}
          notificationId={0}
          isOpen={this.props.addEditAlertIsOpen}
          handleCancel={this.props.handleCancel}
          handleSave={this.props.handleSave}
          isAlert={this.props.isAlert}
          isNew
        />

        <Grid container>
          <Grid item sm={6}>
            {!authentication.isGuest && (
              <>
                {!quoteConditions.isQuote && !quoteConditions.isModifying && (
                  <BreadCrumbGrid>
                    <OrganicLink to="/portfolio">Portfolio</OrganicLink>
                    {fund.name && (
                      <>
                        {" "}
                        /{" "}
                        <OrganicLink to={`/portfolio/funds/${fund.id}`} targetId={fund.id}>
                          {fund.name}
                        </OrganicLink>
                      </>
                    )}
                    {deal.name && (
                      <>
                        {" "}
                        /{" "}
                        <OrganicLink to={`/portfolio/funds/${fund.id}/deals/${deal.id}`} targetId={deal.id}>
                          {deal.name}
                        </OrganicLink>
                      </>
                    )}
                    {loan.lenderName && (
                      <>
                        {" "}
                        / <OrganicLink to={`/portfolio/funds/${fund.id}/deals/${deal.id}/loans/${loan.id}/loanDetailsPage/0`}>{loan.lenderName}</OrganicLink>
                      </>
                    )}
                  </BreadCrumbGrid>
                )}
                {(quoteConditions.isQuote || quoteConditions.isModifying) && (
                  <BreadCrumbGrid>
                    <OrganicLink to="/pipeline">Pipeline</OrganicLink>
                    {deal.name && (
                      <>
                        {" "}
                        /{" "}
                        <OrganicLink to={`/pipeline/active/deals/${deal.id}`} targetId={deal.id}>
                          {deal.name}
                        </OrganicLink>
                      </>
                    )}
                    {loan.lenderName && (
                      <>
                        {" "}
                        / <OrganicLink to={`/loan/${deal.id}/${loan.id}`}>{loan.lenderName}</OrganicLink>
                      </>
                    )}
                  </BreadCrumbGrid>
                )}
              </>
            )}
          </Grid>
          <Grid item sm={6}>
            {authentication.isSystemAdmin && (
              <StyledDelete>
                {" "}
                <DeleteButton onClick={this.props.onDelete} />{" "}
              </StyledDelete>
            )}
          </Grid>
        </Grid>
        <Toolbar disableGutters>
          <Title>{quoteConditions.isQuote ? "Quote Details" : "Loan Details"}</Title>
          {this.renderViewModeButtons(quoteConditions.isQuote)}
          {this.renderEditModeButtons()}
          <Spacer />
          <>
            {hasLoanModificationHistory && !(quoteConditions.isQuote || quoteConditions.isModifying) && (
              <>
                <SecondaryButton onClick={this.showModificationHistory} style={{ marginRight: 5 }}>
                  MODIFICATION HISTORY
                </SecondaryButton>
                <VerticalDivider />
              </>
            )}
            <NotesButton style={{ marginLeft: 5 }} onClick={this.openNotes} />
            <VerticalDivider />
            <AttachmentsButton onClick={this.openAttachments} />
          </>
        </Toolbar>
        <LoanAttachments loanId={loanId} isOpen={isAttachmentsOpen} openAttachments={this.openAttachments} closeAttachments={this.closeAttachments} />
        <LoanNotes loanId={loanId} isOpen={isNotesOpen} openNotes={this.openNotes} closeNotes={this.closeNotes} />
        {this.renderFeatureLockedModel()}
        <QuoteDuplicationConfirmationDialog modalOpen={this.state.isCloneDialogOpen} handleNo={this.closeCloneDialog} handleYes={this.cloneLoan} />
      </ShadowyPageWrapper>
    );
  }
}

const mapStateToProps = ({ authentication, loan }) => ({
  authentication,
  clone: loan.lastCreatedClone,
  loanModificationHistory: loan.loanModificationHistory
});

export default compose(
  connect(mapStateToProps, {
    getNotificationsByDeal,
    updateLoanStatusById,
    getLoanModificationHistory
  })
)(withRouter(LoanDetailsToolbar));
